import { AlertBanner } from '@/components/common/AlertBanner'
import { consentRequiresInteraction } from '@/config/uiConfig'
import type { CanonicalPatient } from '@/ordering/canonicalOrder'
import { COMPARATOR_INCIDENTAL_MODIFIED_NOTICE } from '@/utils/comparatorIncidentalFindings'
import { InfoCircle } from 'flowbite-react-icons/outline'

import type { ComparatorParentConsentConfigSlice } from './comparatorParentConsentConfig'
import { ConsentAccordionGroup } from './ConsentAccordionGroup'

type ProbandConsentPatch = Partial<
  Pick<CanonicalPatient, 'incidentalFindingsSelection' | 'newYorkSampleRetentionOptIn' | 'researchContactConsent'>
>

interface ProbandConsentSectionsProps {
  configs: ComparatorParentConsentConfigSlice
  patient: CanonicalPatient | null | undefined
  onPatch: (patch: ProbandConsentPatch) => void
  /** When true, consent inputs are disabled (e.g. order in progress after submit). */
  readOnly?: boolean
  /** A comparator had opted in before proband opt-out forced comparator opt-out. */
  comparatorIncidentalSelectionMayHaveBeenModified?: boolean
  /**
   * When true, surface inline validation errors on any `requireInteraction`
   * section that is still unanswered (e.g. after a blocked submit). When false
   * (default) no error copy is shown even if a required section is unanswered,
   * so a pristine order doesn't render pre-emptive errors.
   */
  showValidationErrors?: boolean
}

/** Inline validation copy for an unanswered required consent section. */
export const CONSENT_REQUIRED_INTERACTION_ERROR = 'Please select an option to continue.'

/**
 * Tri-state selection for the incidental radios: `true`/`false` when the
 * proband has an explicit opt-in/opt-out, `null` when unanswered. Kept `null`
 * (rather than defaulting to opt-out) so a `requireInteraction` section renders
 * with neither radio selected until the provider chooses.
 */
function incidentalSelectionTriState(patient: CanonicalPatient | null | undefined): boolean | null {
  const sel = patient?.incidentalFindingsSelection
  if (sel === 'opt-in') return true
  if (sel === 'opt-out') return false
  return null
}

/** Tri-state for a boolean opt-in consent: `null` when unset/unanswered. */
function booleanSelectionTriState(value: boolean | null | undefined): boolean | null {
  return value === true ? true : value === false ? false : null
}

/**
 * Inline error message for one section: shown only when validation display is
 * on, the section requires interaction, and it is still unanswered (`null`).
 */
function sectionError(
  showValidationErrors: boolean,
  requireInteraction: boolean,
  selection: boolean | null
): string | undefined {
  return showValidationErrors && requireInteraction && selection === null
    ? CONSENT_REQUIRED_INTERACTION_ERROR
    : undefined
}

export function ProbandConsentSections({
  configs,
  patient,
  onPatch,
  readOnly = false,
  comparatorIncidentalSelectionMayHaveBeenModified = false,
  showValidationErrors = false,
}: ProbandConsentSectionsProps) {
  const incidentalSelection = incidentalSelectionTriState(patient)
  const researchSelection = booleanSelectionTriState(patient?.researchContactConsent)
  const nySelection = booleanSelectionTriState(patient?.newYorkSampleRetentionOptIn)
  return (
    <ConsentAccordionGroup
      configs={configs}
      incidentalOptIn={patient?.incidentalFindingsSelection === 'opt-in'}
      researchOptIn={patient?.researchContactConsent === true}
      nyOptIn={patient?.newYorkSampleRetentionOptIn === true}
      incidentalSelection={incidentalSelection}
      researchSelection={researchSelection}
      nySelection={nySelection}
      incidentalError={sectionError(showValidationErrors, consentRequiresInteraction(configs.incidental), incidentalSelection)}
      researchError={sectionError(showValidationErrors, consentRequiresInteraction(configs.research), researchSelection)}
      nyError={sectionError(showValidationErrors, consentRequiresInteraction(configs.ny), nySelection)}
      onIncidentalChange={(optIn) =>
        onPatch({ incidentalFindingsSelection: optIn ? 'opt-in' : 'opt-out' })
      }
      onResearchChange={(optIn) => onPatch({ researchContactConsent: optIn })}
      onNyChange={(optIn) => onPatch({ newYorkSampleRetentionOptIn: optIn })}
      disabled={readOnly}
      incidentalNotice={
        comparatorIncidentalSelectionMayHaveBeenModified ? (
          <AlertBanner
            variant="info"
            leftIcon={<InfoCircle className="h-4 w-4 shrink-0 text-text-fg-info" aria-hidden />}
          >
            {COMPARATOR_INCIDENTAL_MODIFIED_NOTICE}
          </AlertBanner>
        ) : null
      }
    />
  )
}

export type { ProbandConsentPatch }
